import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SystemApp } from '../constans/SystemConfigurations';
import { ApiUrl } from '../shared/models/apiurl.models';

export interface TrendComment {
  id: number | null;
  username: string;
  text: string;
  timeAgo: string;
  starRating: number;
  parentId: number | null;
  replyToUsername: string | null;
  reactionCounts: Record<string, number>;
  userReaction: string | null;
}

export interface TrendPost {
  productId: number;
  name: string;
  description: string;
  sellerName: string;
  sellerAvatar: string | null;
  imageUrl: string | null;
  videoUrl: string | null;
  price: number;
  avgRating: number;
  ratingsCount: number;
  likesNumber: number;
  userLovesIt: boolean;
  timeAgo: string;
  comments: TrendComment[];
  resourceType: string;
  sellerId: number | null;
  sellerIsVip: boolean;
  sellerIsFollowing: boolean;
  sellerIsSelf: boolean;
  postReactionCounts: Record<string, number>;
  userPostReaction: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class TrendsService {

  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json, text/plain, */*',
      'Authorization': this.apiKey.getJWTToken()
    })
  };

  constructor(private http: HttpClient, private apiKey: ApiUrl) {}

  fetchTrends(page: number): Observable<{ posts: TrendPost[]; hasMore: boolean }> {
    // Despite its name, the backend treats `offset` as a 1-based page number.
    const url = `${SystemApp.base_url_api}/api/socialmedia/trends/?offset=${page}&limit=20`;
    return this.http.get<any>(url, this.options).pipe(
      map(res => {
        const posts: TrendPost[] = (res.results || []).map((j: any) => this.parsePost(j));
        const hasMore: boolean = res.pagination?.has_next ?? false;
        return { posts, hasMore };
      })
    );
  }

  submitReview(productId: number, comment: string, rating: number): Observable<any> {
    const url = `${SystemApp.base_url_api}/insert-review`;
    const body = {
      product_id: productId,
      comments: comment,
      rating,
      cookie: localStorage.getItem('cookie') ?? ''
    };
    return this.http.post<any>(url, body, this.options);
  }

  submitReply(productId: number, comment: string, parentReviewId: number): Observable<any> {
    const url = `${SystemApp.base_url_api}/insert-review`;
    const body = {
      product_id: productId,
      comments: comment,
      parent_review_id: parentReviewId,
      cookie: localStorage.getItem('cookie') ?? ''
    };
    return this.http.post<any>(url, body, this.options);
  }

  reactToComment(commentId: number, reaction: string): Observable<{ reaction_counts: Record<string, number>; user_reaction: string | null }> {
    const url = `${SystemApp.base_url_api}/api/socialmedia/comments/${commentId}/reaction/`;
    return this.http.post<{ reaction_counts: Record<string, number>; user_reaction: string | null }>(url, { reaction }, this.options);
  }

  reactToPost(productId: number, reaction: string): Observable<{ reaction_counts: Record<string, number>; user_reaction: string | null; total: number }> {
    const url = `${SystemApp.base_url_api}/api/socialmedia/posts/${productId}/reaction/`;
    return this.http.post<{ reaction_counts: Record<string, number>; user_reaction: string | null; total: number }>(url, { reaction }, this.options);
  }

  private parsePost(j: any): TrendPost {
    let imageUrl: string | null = null;
    if (j.images && j.images.length > 0) {
      const img = j.images[0];
      imageUrl = img.src || img.image || null;
    }

    const comments: TrendComment[] = (j.ratings || []).map((r: any) => ({
      id: typeof r.id === 'number' ? r.id : null,
      username: r.username || 'משתמש',
      text: r.comments || '',
      timeAgo: TrendsService.toTimeAgo(r.date_created_gmt || ''),
      starRating: r.rating || 0,
      parentId: r.parent_review_id ?? r.parent_id ?? null,
      replyToUsername: r.reply_to_username ?? null,
      reactionCounts: r.reaction_counts || { like: 0, rocket: 0, fire: 0, ice: 0, giggles: 0 },
      userReaction: r.user_reaction || null
    }));

    const user = j.User || {};
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const sellerName = [firstName, lastName].filter(Boolean).join(' ') || j.seller_name || 'מוכר';

    return {
      productId: j.id,
      name: j.name || '',
      description: (j.description || j.FreeText || '').trim(),
      sellerName,
      sellerAvatar: user.avatar || null,
      imageUrl,
      videoUrl: j.video_url || null,
      price: j.price || 0,
      avgRating: j.avg_rating || 0,
      ratingsCount: j.ratings_count || 0,
      likesNumber: j.likes_number || 0,
      userLovesIt: j.user_loves_it || false,
      timeAgo: TrendsService.toTimeAgo(j.date_created_gmt || j.date_modified || j.createTime || ''),
      comments,
      resourceType: j.resourcetype || '',
      sellerId: typeof user.id === 'number' ? user.id : null,
      sellerIsVip: user.is_vip_active === true || user.is_vip === true,
      sellerIsFollowing: user.is_following === true,
      sellerIsSelf: user.is_self === true,
      postReactionCounts: j.post_reaction_counts || { like: 0, rocket: 0, fire: 0, ice: 0, giggles: 0 },
      userPostReaction: j.user_post_reaction || null
    };
  }

  static toTimeAgo(raw: string): string {
    if (!raw) return '';
    const date = new Date(raw);
    if (isNaN(date.getTime())) return '';
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) {
      return `לפני ${diffMin} דק'`;
    }
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) {
      return `לפני ${diffHr} שע'`;
    }
    const diffDays = Math.floor(diffHr / 24);
    return `לפני ${diffDays} ימים`;
  }
}
